import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BulkStringOperationConfig } from "../../../../components/datagrid/types";

interface ColumnDefinition {
  name: string; // Should be in dot-notation format (e.g., "supply.listed")
  type: string;
  clearable?: boolean;
}

interface PrependFormProps {
  columnNames: ColumnDefinition[];
  closeModal: () => void;
  getSelectedRows: () => any[];
  onBulkUpdate: (
    ids: (string | number)[],
    field: string,
    operations: BulkStringOperationConfig[]
  ) => Promise<{ success: boolean; error?: string }>;
  setSnackbar?: (snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error";
  }) => void;
}

/**
 * Formats a field name for display (e.g., "supply.inventory_notes" -> "Inventory Notes")
 */
const formatLabel = (name: string): string => {
  // Extract the column name from dot-notation (e.g., "supply.listed" -> "listed")
  const columnName = name.includes(".") ? name.split(".")[1] : name;
  const words = columnName.split("_");
  return words
    .map((word) => {
      if (word === "amz") return "Amazon";
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export function PrependForm({
  columnNames,
  closeModal,
  getSelectedRows,
  onBulkUpdate,
  setSnackbar,
}: PrependFormProps) {
  const [loading, setLoading] = useState(false);

  // Build initial values and validation schema dynamically
  // Use underscore instead of dot to avoid Formik's nested path interpretation
  const initialValues: Record<string, string | boolean> = {};
  const validationSchema: Record<string, any> = {};

  columnNames.forEach((col) => {
    // Replace dots with underscores for form field keys
    const fieldKey = col.name.replace(/\./g, "_");

    initialValues[`prepend_to_${fieldKey}`] = "";
    initialValues[`remove_from_${fieldKey}`] = "";

    if (col.clearable) {
      initialValues[`clear_${fieldKey}`] = false;
    }

    validationSchema[`prepend_to_${fieldKey}`] =
      col.type === "string"
        ? Yup.string()
        : Yup.number().typeError("A number is required");
    validationSchema[`remove_from_${fieldKey}`] =
      col.type === "string"
        ? Yup.string()
        : Yup.number().typeError("A number is required");
  });

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape(validationSchema),
    onSubmit: async (values) => {
      const selectedRows = getSelectedRows();

      if (selectedRows.length === 0) {
        if (setSnackbar) {
          setSnackbar({
            open: true,
            message: "Please select at least one row",
            severity: "error",
          });
        }
        return;
      }

      setLoading(true);

      try {
        // Extract row IDs
        const ids = selectedRows.map((row) => row.id);

        // Process each column with bulk update
        for (const col of columnNames) {
          const fieldKey = col.name.replace(/\./g, "_");

          if (col.clearable && values[`clear_${fieldKey}`]) {
            const result = await onBulkUpdate(ids, col.name, [
              { operation: "clear", value: "" },
            ]);
            if (!result.success) {
              throw new Error(result.error || `Failed to clear ${col.name}`);
            }
            continue;
          }

          const prependValue = values[`prepend_to_${fieldKey}`] || "";
          const removeValue = values[`remove_from_${fieldKey}`] || "";

          // Build operations array (order: remove first, then prepend)
          const operations: BulkStringOperationConfig[] = [];

          if (removeValue) {
            operations.push({
              operation: "remove",
              value: removeValue as string,
            });
          }

          if (prependValue) {
            operations.push({
              operation: "prepend",
              value: prependValue as string,
            });
          }

          // Only make request if there are operations for this field
          if (operations.length > 0) {
            const result = await onBulkUpdate(ids, col.name, operations);
            if (!result.success) {
              throw new Error(result.error || `Failed to update ${col.name}`);
            }
          }
        }

        closeModal();
      } catch (error) {
        console.error("Error in bulk update:", error);
        if (setSnackbar) {
          setSnackbar({
            open: true,
            message:
              error instanceof Error ? error.message : "Bulk update failed",
            severity: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Dialog open={true} onClose={closeModal} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Prepend to Field</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {columnNames.map((col) => {
              // Convert dot-notation to underscore for form field keys
              const fieldKey = col.name.replace(/\./g, "_");
              const isClearChecked =
                col.clearable && !!formik.values[`clear_${fieldKey}`];
              return (
                <Box key={col.name}>
                  {col.clearable && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formik.values[`clear_${fieldKey}`]}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `clear_${fieldKey}`,
                              e.target.checked
                            )
                          }
                          disabled={loading}
                        />
                      }
                      label={`Clear ${formatLabel(col.name)} field`}
                    />
                  )}
                  <TextField
                    fullWidth
                    id={`prepend_to_${fieldKey}`}
                    name={`prepend_to_${fieldKey}`}
                    label={`Prepend to ${formatLabel(col.name)}`}
                    value={formik.values[`prepend_to_${fieldKey}`]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[`prepend_to_${fieldKey}`] &&
                      Boolean(formik.errors[`prepend_to_${fieldKey}`])
                    }
                    helperText={
                      formik.touched[`prepend_to_${fieldKey}`] &&
                      formik.errors[`prepend_to_${fieldKey}`]
                    }
                    margin="dense"
                    disabled={loading || isClearChecked}
                  />
                  <TextField
                    fullWidth
                    id={`remove_from_${fieldKey}`}
                    name={`remove_from_${fieldKey}`}
                    label={`Remove from ${formatLabel(col.name)}`}
                    value={formik.values[`remove_from_${fieldKey}`]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[`remove_from_${fieldKey}`] &&
                      Boolean(formik.errors[`remove_from_${fieldKey}`])
                    }
                    helperText={
                      formik.touched[`remove_from_${fieldKey}`] &&
                      formik.errors[`remove_from_${fieldKey}`]
                    }
                    margin="dense"
                    disabled={loading || isClearChecked}
                  />
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Updating..." : "Submit"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
